import baseApi from './base';
import { ReviewersTag, ReviewerTag } from './tags';
import { ReviewerStatus, ReviewerType } from '../types/reviewers';

interface ReviewerPayloadType {
  elearningId: string;
  tmsReviewerUserId: string;
  message: string;
  status: ReviewerStatus;
}

interface ReviewerDataResponse {
  id: string;
  tenantId: string;
  lcmsReferenceIds: any;
  reviewerMetadata: {
    firstName: string;
    lastName: string;
    email: string;
  };
  message: string;
  status: ReviewerStatus;
  tmsUserId: string | null;
  tmsReviewerUserId: string | null;
  lastAccessed: string | null;
  isDeleted: boolean | null;
  createdAt: Date;
  modifiedAt: Date;
}

interface TmsReviewerQueryParam {
  page: number;
  searchQueryText: string;
}

const reviewersApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    getCurrentTmsReviewer: build.query<ReviewerDataResponse, string>({
      query: (elearningId) => ({
        url: `users/reviewer`,
        params: {
          'elearning-id': elearningId,
        },
      }),
      providesTags: [ReviewerTag],
    }),
    getTmsReviewers: build.query<any, TmsReviewerQueryParam>({
      // This will be updated when we have API for getting TMS reviewers
      query: (queryParams: TmsReviewerQueryParam) =>
        `reviewers/users?type=tms&page=${queryParams.page}&text=${queryParams.searchQueryText}`,
      providesTags: [ReviewersTag],
    }),
    getReviewers: build.query<ReviewerType[], string>({
      query: (elearningId) => `reviewers?elearning-id=${elearningId}`,
      providesTags: [ReviewersTag],
    }),
    sendReminder: build.mutation<ReviewerType, string>({
      query: (reviewerId: string) => ({
        url: `reviewers/remind/${reviewerId}`,
        method: 'POST',
      }),
      invalidatesTags: [ReviewersTag],
    }),
    deleteReviewer: build.mutation<ReviewerType, string>({
      query: (reviewerId: string) => ({
        url: `reviewers/${reviewerId}`,
        method: 'DELETE',
      }),
      invalidatesTags: [ReviewersTag],
    }),
    createReviewer: build.mutation<ReviewerType, ReviewerPayloadType>({
      query: ({ elearningId, ...payload }) => ({
        url: `reviewers/${elearningId}`,
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: [ReviewersTag],
    }),
    startReview: build.mutation<ReviewerType, string>({
      query: (elearningId) => ({
        url: `reviewers/start`,
        method: 'POST',
        body: { elearningId },
      }),
      invalidatesTags: [ReviewersTag],
    }),
    finaliseReviewer: build.mutation<any, string>({
      query: (elearningId) => ({
        url: `reviewers/finalise`,
        method: 'POST',
        body: { elearningId },
      }),
      invalidatesTags: [ReviewersTag],
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetTmsReviewersQuery,
  useGetReviewersQuery,
  useCreateReviewerMutation,
  useDeleteReviewerMutation,
  useSendReminderMutation,
  useGetCurrentTmsReviewerQuery,
  useStartReviewMutation,
  useFinaliseReviewerMutation,
} = reviewersApi;

export default reviewersApi;
